import axios from "axios";
import AuthState from "@/utils/Auth";

/**
 * A client to the Engage WebApp API that handles authentication and error handling.
 */
export class EngageFhirApiClient {
  constructor() {
    this.axios = axios.create({
      headers: {
        Accept: "application/json"
      }
    });

    const MAX_REQUEST_SIZE_MB = 6;
    const MAX_REQUEST_SIZE_BYTES = MAX_REQUEST_SIZE_MB * 1024 * 1024;

    this.axios.interceptors.request.use(config => {
      if (config.data) {
        const dataSize = JSON.stringify(config.data)?.length;
        if (dataSize > MAX_REQUEST_SIZE_BYTES) {
          const error = new Error(
            `This request cannot be saved because it exceeds the ${MAX_REQUEST_SIZE_MB} MB limit. Request size was ${dataSize?.toLocaleString()} bytes`
          );
          error.statusCode = 413;
          return Promise.reject(error);
        }
      }
      return config;
    });
  }

  /**
   * The constructor runs too early to get the axios base URL from the configuration
   * so call this to get it at request time.
   * @returns The base URL for Engage Webapp FHIR services
   */
  getFhirBaseUrl = () => document.config.engageFhirApiBaseUrl;

  /**
   * Log errors from the API.
   */
  handleError(err) {
    khanSolo.error("Error from Engage FHIR API", err);
    throw err;
  }

  /**
   * Sends a GET request to the Engage WebApp API.
   */
  async get(urlPath, params) {
    try {
      this.axios.defaults.baseURL = this.getFhirBaseUrl();
      const accessToken = await AuthState.accessToken();
      const idToken = AuthState.getIdToken() || accessToken;
      const response = await this.axios.get(urlPath, {
        headers: { Authorization: `Bearer ${accessToken}`, "id-token": idToken },
        params
      });
      return response.data;
    } catch (err) {
      this.handleError(err);
    }
  }

  /**
   * Sends a POST request to the Engage WebApp API.
   */
  async post(urlPath, payload) {
    try {
      this.axios.defaults.baseURL = this.getFhirBaseUrl();
      const accessToken = await AuthState.accessToken();
      const idToken = AuthState.getIdToken() || accessToken;
      const response = await this.axios.post(urlPath, payload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "id-token": idToken,
          "Content-Type": "application/json"
        }
      });
      return response.data;
    } catch (err) {
      this.handleError(err);
    }
  }

  /**
   * Sends a PUT request to the Engage WebApp API.
   */
  async put(urlPath, payload) {
    try {
      this.axios.defaults.baseURL = this.getFhirBaseUrl();
      const accessToken = await AuthState.accessToken();
      const idToken = AuthState.getIdToken() || accessToken;
      const response = await this.axios.put(urlPath, payload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "id-token": idToken,
          "Content-Type": "application/json"
        }
      });
      return response.data;
    } catch (err) {
      this.handleError(err);
    }
  }

  /**
   * Sends a DELETE request to the Engage WebApp API.
   */
  async delete(urlPath) {
    try {
      this.axios.defaults.baseURL = this.getFhirBaseUrl();
      const accessToken = await AuthState.accessToken();
      const idToken = AuthState.getIdToken() || accessToken;
      const response = await this.axios.delete(urlPath, {
        headers: { Authorization: `Bearer ${accessToken}`, "id-token": idToken }
      });
      return response.data;
    } catch (err) {
      this.handleError(err);
    }
  }
}

export const engageFhirApi = new EngageFhirApiClient();
